import { useMemo, useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  FolderOpen,
  User,
  Undo2,
  ChevronRight,
  ChevronDown,
  ListChecks,
  Circle,
  Square,
  CheckSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { TodoItemResponse, TodoSectionResponse } from '@/lib/api';
import { formatItemId } from '../lib/itemId';
import { useTodoChecklist } from '../hooks/useTodoChecklist';

function formatCompletionDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const itemDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (itemDate.getTime() === today.getTime()) return 'Today';
  if (itemDate.getTime() === yesterday.getTime()) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: itemDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function groupByDate(items: TodoItemResponse[]): Map<string, TodoItemResponse[]> {
  const groups = new Map<string, TodoItemResponse[]>();
  for (const item of items) {
    // Cancelled items have no completion date — collect them under one
    // "Cancelled" group (the backend orders them after all completed items, so
    // this group lands at the bottom).
    const key =
      item.status === 'cancelled'
        ? 'Cancelled'
        : item.completedAt
          ? formatCompletionDate(item.completedAt)
          : 'Unknown';
    const existing = groups.get(key);
    if (existing) {
      existing.push(item);
    } else {
      groups.set(key, [item]);
    }
  }
  return groups;
}

interface CompletedTimelineProps {
  items: TodoItemResponse[];
  sections?: TodoSectionResponse[];
  /** Optional pagination — V-1 fetches a fixed limit and omits both. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  loading: boolean;
  /** When true, renders "completed by <name>" using `resolveCompletedBy`
   * to map principalId → display name. Set by the caller depending on
   * whether the context has access to the resolver (private auth'd
   * view does; public view doesn't). */
  showCompletedBy?: boolean;
  /** principalId → display label. Caller supplies (see
   * usePrincipalDisplayNames). Returning '' means "don't render
   * attribution for this item". */
  resolveCompletedBy?: (principalId: string | null | undefined) => string;
  onUncomplete?: (item: TodoItemResponse) => void;
  /** The list's item-id prefix (e.g. "FR") for rendering each item's id. */
  itemKeyPrefix?: string;
  /** All sub-tasks of the list, grouped by parent id — lets a completed task
   * that has sub-tasks expand (via a caret) to show them. Omitted on surfaces
   * without sub-task data. */
  subtasksByParent?: Map<string, TodoItemResponse[]>;
  /** Holder + list ids — needed to lazily read a task's checklist when its row
   * is double-clicked (the "show completed checklist" affordance). */
  holderId?: string | null;
  listId?: string;
}

export function CompletedTimeline({
  items,
  sections = [],
  hasMore = false,
  onLoadMore,
  loading,
  showCompletedBy = false,
  resolveCompletedBy,
  onUncomplete,
  itemKeyPrefix,
  subtasksByParent,
  holderId,
  listId,
}: CompletedTimelineProps) {
  const groups = groupByDate(items);

  // Which completed rows have their sub-tasks / checklist expanded inline.
  const [expandedSubtasks, setExpandedSubtasks] = useState<Set<string>>(new Set());
  const [expandedChecklist, setExpandedChecklist] = useState<Set<string>>(new Set());
  const toggleSubtasks = (id: string) =>
    setExpandedSubtasks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleChecklist = (id: string) =>
    setExpandedChecklist((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Build a lookup from sectionId to category name (only category sections, not timeframes)
  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of sections) {
      if (s.sectionType === 'category') {
        map.set(s.sectionId, s.name);
      }
    }
    return map;
  }, [sections]);

  if (items.length === 0 && !loading) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">No completed items yet.</p>
    );
  }

  return (
    <div className="space-y-6">
      {Array.from(groups.entries()).map(([dateLabel, dateItems]) => (
        <div key={dateLabel}>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">{dateLabel}</h4>
          <div className="divide-y divide-border">
            {dateItems.map((item) => {
              const categoryName = item.sectionId ? categoryMap.get(item.sectionId) : null;
              const subtaskTotal = item.subtaskTotal ?? 0;
              const checklistTotal = item.checklistTotal ?? 0;
              const canExpandSubtasks = !!subtasksByParent && subtaskTotal > 0;
              const canShowChecklist = !!holderId && !!listId && checklistTotal > 0;
              const subtasksOpen = expandedSubtasks.has(item.itemId);
              const checklistOpen = expandedChecklist.has(item.itemId);

              return (
                <div key={item.itemId}>
                  <div
                    className={`group/item flex items-center gap-3 px-3 py-2 ${
                      canShowChecklist ? 'cursor-pointer select-none' : ''
                    }`}
                    onDoubleClick={
                      canShowChecklist ? () => toggleChecklist(item.itemId) : undefined
                    }
                  >
                    {/* Reserved caret slot — expands a completed task's sub-tasks
                        inline; a spacer holds the column when there are none. */}
                    <div className="w-4 flex-shrink-0">
                      {canExpandSubtasks && (
                        <button
                          type="button"
                          className="text-muted-foreground/60 hover:text-muted-foreground"
                          aria-label={
                            subtasksOpen
                              ? `Collapse sub-tasks of "${item.title}"`
                              : `Expand sub-tasks of "${item.title}"`
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSubtasks(item.itemId);
                          }}
                        >
                          {subtasksOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                    {item.status === 'cancelled' ? (
                      <XCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                    )}
                    {itemKeyPrefix && (
                      <code className="text-[10px] font-mono text-muted-foreground flex-shrink-0">
                        {formatItemId(itemKeyPrefix, item.seq)}
                      </code>
                    )}
                    <span
                      className={`text-sm truncate ${
                        item.status === 'cancelled'
                          ? 'text-muted-foreground line-through'
                          : 'text-foreground'
                      }`}
                    >
                      {item.title}
                    </span>
                    {checklistTotal > 0 && (
                      <button
                        type="button"
                        className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0 hover:text-foreground disabled:hover:text-muted-foreground"
                        disabled={!canShowChecklist}
                        aria-label={`${checklistOpen ? 'Hide' : 'Show'} checklist — ${item.checklistDone ?? 0} of ${checklistTotal} done`}
                        title="Checklist (double-click the task to view)"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canShowChecklist) toggleChecklist(item.itemId);
                        }}
                      >
                        <ListChecks className="h-3 w-3" />
                        {item.checklistDone ?? 0}/{checklistTotal}
                      </button>
                    )}
                    {categoryName && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                        <FolderOpen className="h-3 w-3" />
                        {categoryName}
                      </span>
                    )}
                    {showCompletedBy &&
                      item.completedByPrincipalId &&
                      resolveCompletedBy &&
                      (() => {
                        const label = resolveCompletedBy(item.completedByPrincipalId);
                        return label ? (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                            <User className="h-3 w-3" />
                            {label}
                          </span>
                        ) : null;
                      })()}
                    {item.completedAt && (
                      <span className="text-xs text-muted-foreground flex-shrink-0 ml-auto">
                        {formatDateTime(item.completedAt)}
                      </span>
                    )}
                    {onUncomplete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity"
                        onClick={() => onUncomplete(item)}
                        aria-label={`Move "${item.title}" back to todo`}
                        title="Move back to todo"
                      >
                        <Undo2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>

                  {canExpandSubtasks && subtasksOpen && (
                    <ul className="space-y-1 pb-2 pl-11 pr-3">
                      {(subtasksByParent?.get(item.itemId) ?? []).map((st) => (
                        <li key={st.itemId} className="flex items-center gap-2">
                          {st.status === 'cancelled' ? (
                            <XCircle className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          ) : st.status === 'completed' ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                          ) : (
                            <Circle className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          )}
                          {itemKeyPrefix && (
                            <code className="text-[10px] font-mono text-muted-foreground flex-shrink-0">
                              {formatItemId(itemKeyPrefix, st.seq)}
                            </code>
                          )}
                          <span
                            className={`truncate text-xs ${
                              st.status === 'cancelled'
                                ? 'text-muted-foreground line-through'
                                : 'text-foreground'
                            }`}
                          >
                            {st.title}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {canShowChecklist && checklistOpen && (
                    <CompletedChecklistPanel
                      holderId={holderId as string}
                      listId={listId as string}
                      itemId={item.itemId}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {hasMore && onLoadMore && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" size="sm" onClick={onLoadMore} disabled={loading}>
            {loading && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The read-only checklist of one completed task, lazily loaded when its row is
 * expanded (double-clicked). Kept as a child component so the checklist read
 * only fires for the tasks the user actually opens.
 */
function CompletedChecklistPanel({
  holderId,
  listId,
  itemId,
}: {
  holderId: string;
  listId: string;
  itemId: string;
}) {
  const { items, loading } = useTodoChecklist(holderId, listId, itemId);

  if (loading) {
    return <p className="pb-2 pl-11 pr-3 text-xs text-muted-foreground">Loading checklist…</p>;
  }
  if (items.length === 0) {
    return <p className="pb-2 pl-11 pr-3 text-xs text-muted-foreground">No checklist items.</p>;
  }
  return (
    <ul className="space-y-1 pb-2 pl-11 pr-3">
      {items.map((ci) => (
        <li key={ci.checklistItemId} className="flex items-center gap-2 text-xs">
          {ci.isDone ? (
            <CheckSquare className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
          ) : (
            <Square className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          )}
          <span className={ci.isDone ? 'text-muted-foreground line-through' : 'text-foreground'}>
            {ci.title}
          </span>
        </li>
      ))}
    </ul>
  );
}
